import React, { useRef } from "react";
import {
  Box,
  Button,
  Flex,
  Image,
  Text,
  VStack,
  Input,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { BrandedLogo } from "@root/components/branding";

interface LogoImagesTabProps {
  config: IBrandingConfig | null;
  onUpload: (formData: FormData) => Promise<void>;
  onDelete: (logoType: string) => Promise<void>;
  saving: boolean;
}

const LogoUploadSection: React.FC<{
  label: string;
  logoUrl?: string;
  logoType: string;
  onUpload: (formData: FormData) => Promise<void>;
  onDelete: (logoType: string) => Promise<void>;
  saving: boolean;
}> = ({ label, logoUrl, logoType, onUpload, onDelete, saving }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation("common");

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", logoType);
    await onUpload(formData);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="12px"
      p="24px"
    >
      <Text fontWeight={600} fontSize="16px" mb="16px">
        {label}
      </Text>

      <Flex align="center" gap="24px">
        <Box
          width="120px"
          height="80px"
          border="1px dashed"
          borderColor="gray.300"
          borderRadius="8px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="gray.50"
          overflow="hidden"
        >
          {logoUrl ? (
            <Image
              src={logoUrl}
              alt={label}
              maxWidth="100%"
              maxHeight="100%"
              objectFit="contain"
            />
          ) : (
            <Text fontSize="12px" color="gray.400">
              {t("admin.branding.logo.preview")}
            </Text>
          )}
        </Box>

        <VStack align="flex-start" spacing="8px">
          <Input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/x-icon"
            display="none"
            onChange={handleFileSelect}
          />
          <Button
            size="sm"
            colorScheme="blue"
            onClick={() => fileInputRef.current?.click()}
            isLoading={saving}
          >
            {t("admin.branding.logo.upload")}
          </Button>
          {logoUrl && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="red"
              onClick={() => onDelete(logoType)}
              isLoading={saving}
            >
              {t("admin.branding.logo.remove")}
            </Button>
          )}
        </VStack>
      </Flex>
    </Box>
  );
};

export const LogoImagesTab: React.FC<LogoImagesTabProps> = ({
  config,
  onUpload,
  onDelete,
  saving,
}) => {
  const { t } = useTranslation("common");

  return (
    <Flex flexDir="column" gap="24px" p="16px">
      <Box>
        <Text fontWeight={600} fontSize="18px" mb="8px">
          {t("admin.branding.logo.preview")}
        </Text>
        <Flex
          align="center"
          justify="center"
          p="24px"
          bg="gray.50"
          borderRadius="12px"
          border="1px solid"
          borderColor="gray.200"
        >
          <BrandedLogo variant="full" width={150} height={80} />
        </Flex>
      </Box>

      <LogoUploadSection
        label={t("admin.branding.logo.lightLogo")}
        logoUrl={config?.logo?.light}
        logoType="light"
        onUpload={onUpload}
        onDelete={onDelete}
        saving={saving}
      />

      <LogoUploadSection
        label={t("admin.branding.logo.darkLogo")}
        logoUrl={config?.logo?.dark}
        logoType="dark"
        onUpload={onUpload}
        onDelete={onDelete}
        saving={saving}
      />

      <LogoUploadSection
        label={t("admin.branding.logo.favicon")}
        logoUrl={config?.logo?.favicon}
        logoType="favicon"
        onUpload={onUpload}
        onDelete={onDelete}
        saving={saving}
      />
    </Flex>
  );
};
